import { z } from "zod";

import { BooleanSchema } from "../common/dto";

/**
 * Course-related schemas and types
 */

// =============  Course Category Schema =============
export const CourseCategorySchema = z.enum([
  "licenses",
  "theoretical_training",
  "ground_training_checking",
  "flight_training_checking",
  "company_instructor_examiner",
  "flight_instructor",
  "flight_examiner",
  "sign_offs",
  "personal_documents",
]);

export type CourseCategory = z.infer<typeof CourseCategorySchema>;

// Nullable version for use in certificates (allows undefined/null for courses without categories)
export const NullableCourseCategorySchema = CourseCategorySchema.nullable().optional();

// =============  Buffer Zone Schema =============
export const BufferZoneSchema = z.enum(["1", "2", "3"]).transform((val) => parseInt(val, 10));

// Buffer zone value schema (1, 2, or 3 months)
const BufferZoneValueSchema = z.union([z.literal(1), z.literal(2), z.literal(3)]);

export type BufferZone = z.infer<typeof BufferZoneValueSchema>;

// Nullable version for use in certificates (allows undefined/null for courses without buffer zones)
export const NullableBufferZoneSchema = BufferZoneValueSchema.nullable().optional();

// Additional course-related schemas can be added here in the future

// =============  Valid Period Response Schema =============
/**
 * Response schema for valid period endpoints
 * Returns a valid period with its ID and duration in months
 */
export const ValidPeriodResponseSchema = z.object({
  id: z.number().int().positive(),
  validPeriod: z.number().int().min(0),
});

export const PositionValidPeriodResponseSchema = ValidPeriodResponseSchema.extend({
  positionName: z.string(),
});

export type ValidPeriodResponse = z.infer<typeof ValidPeriodResponseSchema>;
export type PositionValidPeriodResponse = z.infer<typeof PositionValidPeriodResponseSchema>;

// =============  Refresher Seasons Response Schema =============
/**
 * Response schema for refresher seasons endpoint
 * Returns an array of season strings
 */
export const RefresherSeasonsResponseSchema = z.array(z.string());

// =============  Course Relations Response Schema =============

export const RelationInfoSchema = z.object({
  crewId: z.number().nullish(),
  roleId: z.number().nullish(),
  positionId: z.number().nullish(),
  operationId: z.number().nullish(),
  designatorId: z.number().nullish(),
  aircraftTypeId: z.number().nullish(),
  groupId: z.number().nullish(),
  hidden: z.boolean().optional(),
});

export type RelationInfo = z.infer<typeof RelationInfoSchema>;

export const CourseWithRelationSchema = z
  .object({
    id: z.number(),
    name: z.string(),
    shortName: z.string().optional(),
    displayName: z.string().optional(),
    type: z.string().optional(),
    isConfigured: z.boolean(),
    visible: z.number().optional(),
    showAtAllCourses: z.number().optional(),
    startDate: z.number().optional(),
  })
  .merge(RelationInfoSchema)
  .passthrough();

export type CourseWithRelation = z.infer<typeof CourseWithRelationSchema>;

export const RelationRoleSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const RelationPositionSchema = z.object({
  id: z.number(),
  name: z.string(),
  roleId: z.number(),
  operations: z.array(z.number()),
});

export type RelationPosition = z.infer<typeof RelationPositionSchema>;

export const RelationOperationSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export type RelationOperation = z.infer<typeof RelationOperationSchema>;

export const RelationAircraftDesignatorSchema = z.object({
  id: z.number(),
  name: z.string(),
  specializations: z.unknown().nullable(),
});

export const RelationAircraftTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
  designatorId: z.number().nullable(),
});

export const RelationsSchema = z.object({
  roles: z.array(RelationRoleSchema),
  positions: z.array(RelationPositionSchema),
  operations: z.array(RelationOperationSchema),
  aircraftDesignators: z.array(RelationAircraftDesignatorSchema),
  aircraftTypes: z.array(RelationAircraftTypeSchema),
  crewAircraft: z.array(z.number()).optional(),
});

export type Relations = z.infer<typeof RelationsSchema>;

export const RelationGroupEntrySchema = RelationInfoSchema.extend({
  name: z.string(),
  courses: z.array(z.unknown()),
});

export type RelationGroupEntry = z.infer<typeof RelationGroupEntrySchema>;

export const AvailableCourseOptionSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export type AvailableCourseOption = z.infer<typeof AvailableCourseOptionSchema>;

export const AvailableCustomCourseOptionSchema = AvailableCourseOptionSchema.extend({
  isCustom: z.literal(true),
});

export type AvailableCustomCourseOption = z.infer<typeof AvailableCustomCourseOptionSchema>;

export const AvailableCourseGroupOptionSchema = AvailableCourseOptionSchema.extend({
  group: z.literal(true),
});

export type AvailableCourseGroupOption = z.infer<typeof AvailableCourseGroupOptionSchema>;

export const AvailableCoursesResponseSchema = z.object({
  courses: z.array(AvailableCourseOptionSchema),
  customCourses: z.array(AvailableCustomCourseOptionSchema),
  groups: z.array(AvailableCourseGroupOptionSchema),
});

export type AvailableCoursesResponse = z.infer<typeof AvailableCoursesResponseSchema>;

// =============  Relation Tree (crew catalog) Schemas =============
/**
 * Server-assembled course relation tree (roles → positions → operations, with
 * aircraft nesting at every level) — the catalog counterpart of the per-user
 * compliance tree. Returned by GET /courses/relation-tree.
 *
 * Node buckets mirror the flat CourseRelationsResponse rows: each node carries
 * the course/customCourse/group relation rows attached at exactly that level
 * (level-exclusive, deduplicated on id:roleId:positionId:operationId:
 * designatorId:aircraftTypeId:hidden).
 */

export const RelationTreeAircraftTypeNodeSchema = z.object({
  id: z.number(),
  kind: z.literal("type"),
  name: z.string(),
  courses: z.array(CourseWithRelationSchema),
  customCourses: z.array(CourseWithRelationSchema),
  groups: z.array(RelationGroupEntrySchema),
});

export type RelationTreeAircraftTypeNode = z.infer<typeof RelationTreeAircraftTypeNodeSchema>;

export const RelationTreeAircraftNodeSchema = z.object({
  id: z.number(),
  kind: z.enum(["designator", "type"]),
  name: z.string(),
  courses: z.array(CourseWithRelationSchema),
  customCourses: z.array(CourseWithRelationSchema),
  groups: z.array(RelationGroupEntrySchema),
  // Present on designator nodes (may be empty); absent on standalone type nodes.
  types: z.array(RelationTreeAircraftTypeNodeSchema).optional(),
});

export type RelationTreeAircraftNode = z.infer<typeof RelationTreeAircraftNodeSchema>;

export const RelationTreeOperationNodeSchema = z.object({
  id: z.number(),
  name: z.string(),
  courses: z.array(CourseWithRelationSchema),
  customCourses: z.array(CourseWithRelationSchema),
  groups: z.array(RelationGroupEntrySchema),
  aircraft: z.array(RelationTreeAircraftNodeSchema),
});

export type RelationTreeOperationNode = z.infer<typeof RelationTreeOperationNodeSchema>;

export const RelationTreePositionNodeSchema = z.object({
  id: z.number(),
  name: z.string(),
  roleId: z.number(),
  courses: z.array(CourseWithRelationSchema),
  customCourses: z.array(CourseWithRelationSchema),
  groups: z.array(RelationGroupEntrySchema),
  operations: z.array(RelationTreeOperationNodeSchema),
  aircraft: z.array(RelationTreeAircraftNodeSchema),
});

export type RelationTreePositionNode = z.infer<typeof RelationTreePositionNodeSchema>;

export const RelationTreeRoleNodeSchema = z.object({
  id: z.number(),
  name: z.string(),
  courses: z.array(CourseWithRelationSchema),
  customCourses: z.array(CourseWithRelationSchema),
  groups: z.array(RelationGroupEntrySchema),
  positions: z.array(RelationTreePositionNodeSchema),
  aircraft: z.array(RelationTreeAircraftNodeSchema),
});

export type RelationTreeRoleNode = z.infer<typeof RelationTreeRoleNodeSchema>;

export const RelationTreeResponseSchema = z.object({
  // Assembled catalog tree: empty positions/operations pruned, roles kept
  // (role-level pruning stays a client concern).
  tree: z.array(RelationTreeRoleNodeSchema),
  // Unpruned flat catalog (same block as CourseRelationsResponse.relations) —
  // feeds form dropdown cascades without a second request.
  relations: RelationsSchema,
});

export type RelationTreeResponse = z.infer<typeof RelationTreeResponseSchema>;

export const GetRelationTreeQuerySchema = z.object({
  crewId: z.coerce.number().optional(),
  includeHidden: BooleanSchema.optional(),
});

export type GetRelationTreeQuery = z.infer<typeof GetRelationTreeQuerySchema>;
